import { Glyph } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import { Header } from '@/ui/core/header'
import { Text } from '@/ui/core/typography'
import { cn } from '@/ui/utils'
import { type ReactNode, createContext, useContext, useEffect } from 'react'
import { View, type ViewProps } from 'react-native'
import Animated, {
	Easing,
	useAnimatedStyle,
	useReducedMotion,
	useSharedValue,
	withDelay,
	withRepeat,
	withSpring,
	withTiming,
} from 'react-native-reanimated'

/**
 * A note left on the counter. The unit of the app: everything Wally hands you (a visit, a
 * report, a checklist) sits on one. The colour is the edge: no border, no shadow. `note` for
 * most things, `blush` where the note holds white elements of its own and cannot be white
 * itself, `ink` (pink type) for the visit and the results, `accent` for the one promotion,
 * `mint` for good news.
 */
export type NoteTone = 'note' | 'white' | 'blush' | 'ink' | 'accent' | 'mint'

const TONE: Record<NoteTone, string> = {
	note: 'bg-note',
	white: 'bg-white',
	blush: 'bg-blush',
	ink: 'bg-ink',
	accent: 'bg-accent',
	mint: 'bg-mint',
}

/** What the type on a note of this tone is coloured; children read it via `useNoteTone`. */
export const NoteToneContext = createContext<NoteTone>('note')
export const useNoteTone = () => useContext(NoteToneContext)

/** The text colour that reads on a note of this tone. */
export const inkOn = (tone: NoteTone) =>
	tone === 'ink' ? 'ground' : tone === 'accent' ? 'white' : 'ink'

export const Note = ({
	tone = 'note',
	padded = true,
	className,
	children,
	...props
}: ViewProps & {
	tone?: NoteTone
	padded?: boolean
	className?: string
	children: ReactNode
}) => (
	<NoteToneContext.Provider value={tone}>
		<View
			className={cn(
				'rounded-note overflow-hidden',
				TONE[tone],
				padded && 'p-6',
				className
			)}
			style={{ borderCurve: 'continuous' }}
			{...props}
		>
			{children}
		</View>
	</NoteToneContext.Provider>
)

/**
 * A sticker on the corner of a note, for the small joys: "See you Friday!", "All good". Mint,
 * tilted like a real one, and the only place an exclamation mark is allowed. It pops in a beat
 * after the note; under Reduce Motion it is simply there.
 */
export const Sticker = ({
	children,
	tone = 'mint',
	className,
	delay = 500,
}: {
	children: string
	tone?: 'mint' | 'accent' | 'ink'
	className?: string
	delay?: number
}) => {
	const reduceMotion = useReducedMotion()
	const progress = useSharedValue(reduceMotion ? 1 : 0)

	useEffect(() => {
		if (reduceMotion) return
		progress.value = withDelay(delay, withSpring(1, { damping: 11, stiffness: 180 }))
	}, [delay, progress, reduceMotion])

	const style = useAnimatedStyle(() => ({
		opacity: progress.value,
		transform: [
			{ rotate: `${5 - (1 - progress.value) * 14}deg` },
			{ scale: 0.6 + progress.value * 0.4 },
		],
	}))

	const bg = { mint: 'bg-mint', accent: 'bg-accent', ink: 'bg-ink' }[tone]
	const fg = tone === 'mint' ? colors.ink : colors.white

	return (
		<Animated.View
			pointerEvents="none"
			className={cn(
				'absolute flex-row items-center gap-1.5 rounded-full px-3.5 h-[34px]',
				bg,
				className
			)}
			style={[{ boxShadow: '0 2px 0 rgba(0,0,0,0.12)' }, style]}
		>
			<Glyph name="sparkle" size={13} color={fg} />
			<Text size="body2" weight="bold" style={{ color: fg }}>
				{children}
			</Text>
		</Animated.View>
	)
}

/**
 * The website's marquee, across the top of a note: "SEE YOU FRIDAY ✦ SEE YOU FRIDAY ✦". Slow
 * enough to read at a glance and not so fast it nags. Under Reduce Motion it holds still.
 */
export const Ribbon = ({
	children,
	tone = 'mint',
}: { children: string; tone?: 'mint' | 'accent' | 'ground' }) => {
	const reduceMotion = useReducedMotion()
	const x = useSharedValue(0)
	const started = useSharedValue(false)

	const start = (width: number) => {
		if (reduceMotion || started.value || !width) return
		started.value = true
		x.value = withRepeat(
			withTiming(-width, { duration: width * 28, easing: Easing.linear }),
			-1,
			false
		)
	}

	const style = useAnimatedStyle(() => ({ transform: [{ translateX: x.value }] }))
	const bg = { mint: 'bg-mint', accent: 'bg-accent', ground: 'bg-ground' }[tone]
	const fg = tone === 'accent' ? colors.white : colors.ink
	const phrase = `${children.toUpperCase()}   ✦   `.repeat(4)

	return (
		<View className={cn('h-[34px] justify-center overflow-hidden', bg)}>
			<Animated.View className="flex-row" style={style}>
				{[0, 1].map((copy) => (
					<Header
						key={copy}
						variant="h7"
						numberOfLines={1}
						style={{ color: fg }}
						onLayout={copy === 0 ? (e) => start(e.nativeEvent.layout.width) : undefined}
					>
						{phrase}
					</Header>
				))}
			</Animated.View>
		</View>
	)
}
